import { useIntl } from 'react-intl';

import { Offer, AggregateRating } from 'schema-dts';

import { StockAvailability } from '@notino/shared/definitions/types';

import { Product } from '../../model';
import { IConditionalVoucherDiscount } from '../ProductBaseInfo/VoucherDiscount/ConditionalVoucherDiscount';
import { resolveVoucherDiscount } from '../ProductBaseInfo/VoucherDiscount/useResolvedVoucherDiscount';
import { isConditionalDiscount } from '../ProductBaseInfo/VoucherDiscount/utils';
import { IVoucherDiscount } from '../ProductBaseInfo/VoucherDiscount/VoucherDiscount';

const SOURCE_KEY = 'order_2k';

const getImageAddress = (src: string, imageDomainPath: string) =>
  `${imageDomainPath}${SOURCE_KEY}/${src}`;

export const getMainImageSrc = (
  variant: Product['variants'][number],
  imageDomainPath: string
): string | null =>
  variant.media && variant.media.mainImage
    ? getImageAddress(variant.media.mainImage.src ?? '', imageDomainPath)
    : null;

export const getImagesSrc = (
  variant: Product['variants'][number],
  imageDomainPath: string
): string[] => {
  const result: string[] = [];

  if (variant.media) {
    const mainImageSrc = getMainImageSrc(variant, imageDomainPath);
    if (mainImageSrc) {
      result.push(mainImageSrc);
    }
    if (variant.media.others) {
      variant.media.others.forEach((image) =>
        result.push(getImageAddress(image.src ?? '', imageDomainPath))
      );
    }
  }

  return result;
};

const getItemAvailability = (variant: Product['variants'][number]) => {
  if (variant.availability.stockAvailability === StockAvailability.OutOfStock) {
    return 'https://schema.org/OutOfStock';
  }
  return 'https://schema.org/InStock';
};

type GetVariantOfferReturn = {
  itemCountry: string;
  priceValidFrom?: string;
  priceValidUntil?: string;
} & Offer;

export type GetVariantOfferArgs = {
  variant: Product['variants'][number];
  product: Product;
  imageDomainPath: string;
  itemCountry: string;
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  convertToEur?: (value: number) => string;
  isBulgaria?: boolean;
};

const bgPriceValidUntil = '2026-12-31';
const bgPriceValidFrom = '2025-08-08';

export const getVariantOffer = ({
  imageDomainPath,
  itemCountry,
  variant,
  product,
  formatMessage,
  convertToEur,
  isBulgaria,
}: GetVariantOfferArgs): GetVariantOfferReturn => {
  const variantMainImageSrc = getMainImageSrc(variant, imageDomainPath);
  const discount: IVoucherDiscount = variant.attributes.VoucherDiscount;
  const price = discount ? discount.discountedPrice : variant.price.value ?? 0;
  const eurPrice = convertToEur?.(price)?.replace(',', '.');
  const areaServedPlace = formatMessage({
    id: 'pd.jsonLd.offer.areaServed.place',
    defaultMessage: 'Czech Republic',
  });

  return {
    '@type': 'Offer',
    name: `${product.brand?.name ?? ''} ${variant.name ?? ''} ${
      variant.additionalInfo || ''
    }`,
    areaServed: {
      '@type': 'Place',
      name: areaServedPlace,
    },
    availability: getItemAvailability(variant),
    ...(variant.price && {
      price: eurPrice ?? price,
      priceCurrency: eurPrice ? 'EUR' : variant.price.currency ?? '',
    }),
    itemCountry,
    sku: variant.productCode,
    url: variant.url ?? undefined,
    ...(variantMainImageSrc && {
      image: variantMainImageSrc,
    }),
    ...(isBulgaria && !eurPrice && { priceValidUntil: bgPriceValidUntil }),
    ...(isBulgaria && eurPrice && { priceValidFrom: bgPriceValidFrom }),
  };
};

const getConditionalDiscountPrice = (voucher: IConditionalVoucherDiscount) => {
  return Math.min(
    ...voucher.discountConditions.map(({ discountedPrice }) => discountedPrice)
  );
};

export const getDiscountedVariantOffer = (
  offerArgs: GetVariantOfferArgs
): GetVariantOfferReturn => {
  const voucher = resolveVoucherDiscount(offerArgs.variant);
  const { convertToEur, isBulgaria } = offerArgs;

  const voucherPrice =
    voucher && isConditionalDiscount(voucher)
      ? getConditionalDiscountPrice(voucher)
      : voucher?.discountedPrice ?? 0;

  const validUntil =
    voucher && isConditionalDiscount(voucher) ? voucher.validUntil : null;

  const eurVoucherPrice = convertToEur?.(voucherPrice)?.replace(',', '.');

  const { priceValidUntil: _priceValidUntil, ...restVariantOffers } =
    getVariantOffer(offerArgs);

  return {
    ...restVariantOffers,
    price: eurVoucherPrice ?? voucherPrice,

    ...(validUntil && {
      priceValidUntil: validUntil,
    }),

    ...(isBulgaria &&
      !eurVoucherPrice && { priceValidUntil: bgPriceValidUntil }),

    ...(isBulgaria && eurVoucherPrice && { priceValidFrom: bgPriceValidFrom }),
  };
};

interface GetVariantOffersByFilterArgs
  extends Omit<GetVariantOfferArgs, 'variant'> {
  offerCallback: (args: GetVariantOfferArgs) => GetVariantOfferReturn;
  filterCondition?: (variant: Product['variants'][number]) => boolean;
  convertToEur?: (value: number) => string;
  isBulgaria?: boolean;
}

export const getVariantOffersByFilter = ({
  formatMessage,
  imageDomainPath,
  itemCountry,
  offerCallback,
  product,
  filterCondition,
  convertToEur,
  isBulgaria,
}: GetVariantOffersByFilterArgs) => {
  const filteredVariants = product.variants
    .filter((variant) => (variant.price?.value ?? 0) > 0)
    .filter((variant) => (filterCondition ? filterCondition(variant) : true));

  const baseOffers = filteredVariants.map((variant) =>
    offerCallback({
      variant,
      product,
      imageDomainPath,
      itemCountry,
      formatMessage,
      isBulgaria,
    })
  );

  if (convertToEur) {
    const eurOffers = filteredVariants.map((variant) =>
      offerCallback({
        variant,
        product,
        imageDomainPath,
        itemCountry,
        formatMessage,
        convertToEur,
        isBulgaria,
      })
    );
    return [...baseOffers, ...eurOffers];
  }

  return baseOffers;
};

export const getAggregateRating = (
  reviewOverview: NonNullable<Product['reviewOverview']>
): AggregateRating => ({
  '@type': 'AggregateRating',
  bestRating: '5',
  worstRating: '1',
  ratingValue: reviewOverview.rating,
  ratingCount: reviewOverview.count,
});
